import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { StoreSettingsForm } from "@/components/store-management/StoreSettingsForm";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Edit, Eye, CheckCircle, XCircle, PlusCircle, Package } from "lucide-react";
import { toast } from "sonner";
import { StoreProfile } from "@/types/store";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { useProducts } from "@/hooks/useProducts";
import { ProductCard } from "@/components/product/ProductCard";
import { loadGoogleMapsApi } from "@/utils/googleMaps";

export default function StoreDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState<"settings" | "products">("settings");
  const location = useLocation();

  useEffect(() => {
    setIsEditMode(location.pathname.endsWith('/edit'));
  }, [location.pathname]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) {
      navigate("/");
    }
  }, [user, isAdmin, authLoading, navigate]);

  const { data: store, isLoading, error } = useQuery<StoreProfile | null>({
    queryKey: ['admin-store-details', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('store_profiles')
        .select('*')
        .eq('id', id)
        .single();
      if (error) {
        console.error("Error fetching store details for admin:", error);
        throw error;
      }
      return data;
    },
    enabled: !authLoading && !!user && !!isAdmin && !!id
  });

  const updateStore = useMutation({
    mutationFn: async (data: any) => {
      console.log("Starting store update for store ID:", id);

      // Only send the fields we want to update — never send immutable fields like id, created_at, florist_id
      const updatePayload: Record<string, any> = {
        store_name: data.store_name?.trim() || null,
        description: data.description?.trim() || null,
        phone: data.phone?.trim() || null,
        email: data.email?.trim() || null,
        storefront_image_url: data.storefront_image_url || null,
        cover_image_url: data.cover_image_url || null,
        use_storefront_as_cover: data.use_storefront_as_cover ?? false,
        business_hours: data.business_hours || {},
        unit_number: data.unit_number?.trim() || null,
        street_number: data.street_number?.trim() || null,
        street_name: data.street_name?.trim() || null,
        suburb: data.suburb?.trim() || null,
        state: data.state?.trim() || null,
        postcode: data.postcode?.trim() || null,
        country: data.country?.trim() || "Australia",
        delivery_radius: data.delivery_radius ?? 10,
        delivery_fee: data.delivery_fee !== undefined ? data.delivery_fee : null,
        minimum_order_amount: data.minimum_order_amount !== undefined ? data.minimum_order_amount : null,
        distance_type: data.distance_type || "radius",
        use_multiple_zones: data.use_multiple_zones ?? false,
        delivery_zones: data.delivery_zones || [],
        same_day_enabled: data.same_day_enabled ?? false,
        delivery_days: data.delivery_days || [],
        delivery_cutoff_times: data.delivery_cutoff_times || {},
        non_sameday_enabled: data.non_sameday_enabled ?? true,
        non_sameday_days: data.non_sameday_days || [],
        non_sameday_time_slots: data.non_sameday_time_slots || [],
        blackout_dates: data.blackout_dates || [],
        updated_at: new Date().toISOString(),
      };

      // Geocode the address if we have enough address fields
      if (updatePayload.street_number && updatePayload.street_name && updatePayload.suburb && updatePayload.state) {
        try {
          await loadGoogleMapsApi();
          if (typeof window.google !== 'undefined' && window.google.maps?.Geocoder) {
            const geocoder = new window.google.maps.Geocoder();
            const addressParts = [
              updatePayload.unit_number,
              updatePayload.street_number,
              updatePayload.street_name,
              updatePayload.suburb,
              updatePayload.state,
              updatePayload.country || "Australia"
            ].filter(Boolean);
            const address = addressParts.join(' ');
            console.log("Admin geocoding address:", address);

            const result = await geocoder.geocode({ address });
            if (result.results.length > 0) {
              const location = result.results[0].geometry.location;
              updatePayload.coordinates = {
                lat: location.lat(),
                lng: location.lng()
              };
              updatePayload.geocoded_address = JSON.stringify({
                formatted_address: result.results[0].formatted_address,
                address_components: result.results[0].address_components,
                place_id: result.results[0].place_id,
                geometry: result.results[0].geometry,
                types: result.results[0].types,
                suburb: updatePayload.suburb,
                state: updatePayload.state
              });
              console.log("Admin geocoded coordinates:", updatePayload.coordinates);
            } else {
              console.warn("No geocoding results for:", address);
            }
          } else {
            console.warn("Google Maps Geocoder not available for admin geocoding");
          }
        } catch (error) {
          console.error("Admin geocoding error:", error);
        }
      }

      console.log("Update payload for store:", updatePayload);

      const { data: result, error } = await supabase
        .from('store_profiles')
        .update(updatePayload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error("Error updating store:", error);
        throw error;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-store-details', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-stores'] });
    }
  });

  // Approve Store: Sets approval_status to 'approved' and store_status to 'active'
  const approveStoreMutation = useMutation({
    mutationFn: async (storeId: string) => {
      if (!user?.id) throw new Error("User ID not found");
      const { data, error } = await supabase
        .from('store_profiles')
        .update({
          approval_status: 'approved',
          store_status: 'active', // Ensure store becomes active on approval
          approved_at: new Date().toISOString(),
          approved_by: user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', storeId);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-store-details', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-stores'] });
      toast.success("Store approved and activated successfully");
    },
    onError: (error) => {
      console.error("Error approving store:", error);
      toast.error("Failed to approve store");
    }
  });

  // Set Store to Pending: Sets approval_status to 'pending' (and optionally store_status to 'inactive')
  const setStoreToPendingMutation = useMutation({
    mutationFn: async (storeId: string) => {
      const { error } = await supabase
        .from('store_profiles')
        .update({ approval_status: 'pending', store_status: 'pending', updated_at: new Date().toISOString() })
        .eq('id', storeId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-store-details', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-stores'] });
      toast.success("Store status set to pending. It will not be publicly visible.");
    },
    onError: (error) => {
      console.error("Error setting store to pending:", error);
      toast.error("Failed to update store status");
    }
  });

  // Products for this store
  const {
    products,
    isLoading: productsLoading,
    error: productsError,
  } = useProducts({
    storeId: id || "",
    enabled: !authLoading && !!user && !!isAdmin && !!id,
  });

  const handleDeleteProduct = async (productId: string) => {
    try {
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", productId);
      if (error) throw error;
      toast.success("Product deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["products", id] });
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  const handleStoreUpdate = async (data: any) => {
    try {
      // IMPORTANT: Make sure we're using the correct database field names
      // This transforms any legacy code that might still use non_sameday_slots
      // to the correct field name non_sameday_time_slots
      const updatedData = {
        ...data,
        // Ensure we're using the correct field name for time slots
        non_sameday_time_slots: data.non_sameday_time_slots || [],
        // Remove any legacy field names to prevent confusion
        non_sameday_slots: undefined,
      };
      
      await updateStore.mutateAsync(updatedData);
      toast.success("Store details updated successfully");
    } catch (error) {
      console.error("Error updating store:", error);
      toast.error("Failed to update store details");
    }
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      navigate(`/admin/stores/${id}`);
    } else {
      navigate(`/admin/stores/${id}/edit`);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!store) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="max-w-[1200px] mx-auto text-center">
            <p className="text-red-500">Store not found</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            className="gap-2 bg-[#967F81] hover:bg-[#843329]/10 hover:text-brand-primary"
            onClick={() => navigate('/admin/stores')}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Stores
          </Button>

          <div className="flex items-center gap-2">
            {store && store.approval_status === 'pending' && (
              <Button
                variant="outline"
                className="gap-2 bg-green-50 text-green-600 hover:bg-green-100 border-green-200"
                onClick={() => store && approveStoreMutation.mutate(store.id)}
                disabled={approveStoreMutation.isPending}
              >
                {approveStoreMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Approve & Activate Store
              </Button>
            )}
            {store && store.approval_status === 'approved' && (
              <Button
                variant="outline"
                className="gap-2 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-300"
                onClick={() => store && setStoreToPendingMutation.mutate(store.id)}
                disabled={setStoreToPendingMutation.isPending}
              >
                {setStoreToPendingMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                Set to Pending (Unlist)
              </Button>
            )}
            <Button
              variant="outline"
              className="gap-2 bg-[#967F81] hover:bg-[#843329]/10 hover:text-brand-primary"
              onClick={toggleEditMode}
            >
              {isEditMode ? (
                <>
                  <Eye className="h-4 w-4" />
                  View Mode
                </>
              ) : (
                <>
                  <Edit className="h-4 w-4" />
                  Edit Mode
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#4A4F41]">{store.store_name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <p className="text-muted-foreground">
              {activeTab === "settings"
                ? (isEditMode ? 'Edit store details' : 'View store details')
                : 'Manage products'}
            </p>
            {store.approval_status === 'pending' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Pending Approval
              </span>
            )}
            {store.approval_status === 'approved' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Approved
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab("settings")}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "settings"
                ? "border-[#967F81] text-[#967F81]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Store Settings
          </button>
          <button
            onClick={() => setActiveTab("products")}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "products"
                ? "border-[#967F81] text-[#967F81]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Package className="h-4 w-4" />
            Products ({products?.length || 0})
          </button>
        </div>

        {activeTab === "settings" && (
          <div className="bg-white rounded-lg shadow-sm border p-1">
            <StoreSettingsForm
              initialData={store}
              onSave={handleStoreUpdate}
              isLoading={updateStore.isPending}
              readOnly={!isEditMode}
            />
          </div>
        )}

        {activeTab === "products" && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Products</h2>
              <Button
                onClick={() => navigate(`/admin/stores/${id}/products/new`)}
                className="bg-[#967F81] hover:bg-[#843329]"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>

            {productsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#967F81]" />
              </div>
            ) : productsError ? (
              <div className="bg-red-50 p-6 rounded-lg border border-red-100 text-center text-red-500">
                Failed to load products. Please try again.
              </div>
            ) : !products || products.length === 0 ? (
              <div className="py-12 bg-gray-50 rounded-lg border border-gray-200 text-center">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500 font-medium mb-4">No products yet</p>
                <Button
                  onClick={() => navigate(`/admin/stores/${id}/products/new`)}
                  className="bg-[#967F81] hover:bg-[#843329]"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add First Product
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    view="grid"
                    onDelete={handleDeleteProduct}
                    editUrl={`/admin/stores/${id}/products/edit/${product.id}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
} 